/**
 * profileSync.js
 *
 * Syncs app profile configuration between the browser's localStorage (fast
 * synchronous read layer) and the backend SQLite database (persistent source
 * of truth shared across devices / browsers).
 *
 * Design:
 *   - localStorage is always the read layer — appProfiles.js reads from it
 *     synchronously, so no async refactoring is needed anywhere.
 *   - The DB is the write-through destination: every profile mutation calls
 *     profileSync.push() (fire-and-forget) to persist the change server-side.
 *   - On app boot, profileSync.pull() fetches the DB state and merges it into
 *     localStorage, so the freshest config is always available.
 *
 * localStorage keys managed here:
 *   adlensfx_initialized   — Set<'appId/profileId'>  (JSON array)
 *   adlensfx_custom_apps   — custom apps + builtin overrides (JSON object)
 *   adlensfx_baselines     — capture baselines keyed by 'appId/profileId'
 */

import API_BASE from '../apiBase';
import { authHeader, handleAuthResponse, notifyAuthExpired } from '../api/authFetch';

/* ─── Auth helper ──────────────────────────────────────────────────────────── */
function authHeaders() {
  return { 'Content-Type': 'application/json', ...authHeader() };
}

/** fetch wrapper: consume any rotated token (sliding session — the debounced
 *  profile push is a natural periodic slide) and route a 401 through the shared
 *  graceful re-auth flow. */
async function afetch(url, opts) {
  const res = await fetch(url, opts);
  handleAuthResponse(res);
  if (res.status === 401) notifyAuthExpired();
  return res;
}

/* ─── Pull: DB → localStorage ─────────────────────────────────────────────── */

/**
 * Fetch stored profiles from the backend and merge into localStorage.
 * Call once on app mount. Safe to call multiple times — idempotent.
 *
 * Merge strategy:
 *   - DB data wins for initialized set and customApps (server is source of truth)
 *   - Local items not yet synced are preserved (union, not replace)
 */
export async function pullProfiles() {
  try {
    const res = await afetch(`${API_BASE}/api/profiles`, { headers: authHeaders() });
    if (!res.ok) return;  // offline or not authed — stay with localStorage
    const data = await res.json();

    // ── initialized set ──────────────────────────────────────────────────────
    if (Array.isArray(data.initialized)) {
      const local  = _readSet('adlensfx_initialized');
      // Union: DB entries are additive — never remove local entries.
      // This preserves user-intentional deletions while recovering from
      // a fresh DB overwriting an empty local state. No built-in profiles ship
      // pre-initialized (the tool is app-agnostic — see BUILTIN_INITIALIZED in
      // appProfiles.js), so a fresh install seeds nothing here; only the
      // synthesized `generic` app exists out of the box.
      const merged = new Set([...local, ...data.initialized]);
      _writeSet('adlensfx_initialized', merged);
    }

    // ── removed (hidden) built-in profiles ───────────────────────────────────
    if (Array.isArray(data.removedBuiltins)) {
      const local  = _readSet('adlensfx_removed_builtins');
      const merged  = new Set([...local, ...data.removedBuiltins]);
      _writeSet('adlensfx_removed_builtins', merged);
    }

    // ── custom apps (includes builtin overrides) ─────────────────────────────
    if (data.customApps && typeof data.customApps === 'object') {
      const local = _readObj('adlensfx_custom_apps');
      // DB entries win per app-id; local custom entries not in DB are kept
      const merged = { ...local, ...data.customApps };
      _writeObj('adlensfx_custom_apps', merged);
    }

    // ── baselines ────────────────────────────────────────────────────────────
    if (data.baselines && typeof data.baselines === 'object') {
      const local = _readObj('adlensfx_baselines');
      const merged = { ...local, ...data.baselines };
      _writeObj('adlensfx_baselines', merged);
      // Expand individual baseline keys for captureSession compatibility
      Object.entries(merged).forEach(([compositeId, bl]) => {
        try { localStorage.setItem(`adlensfx_baseline_${compositeId.replace('/', '_')}`, JSON.stringify(bl)); } catch {}
      });
    }
  } catch {
    // Network error — silently continue with localStorage
  }
}

/* ─── Push: localStorage → DB ─────────────────────────────────────────────── */

let _pushTimer = null;

/**
 * Debounced push — waits 1.5s after the last write before sending to DB.
 * This prevents a burst of writes (e.g. during a capture session) from
 * hammering the API.
 */
export function push() {
  clearTimeout(_pushTimer);
  _pushTimer = setTimeout(_doPush, 1500);
}

async function _doPush() {
  try {
    const initialized    = [..._readSet('adlensfx_initialized')];
    const removedBuiltins = [..._readSet('adlensfx_removed_builtins')];
    const customApps      = _readObj('adlensfx_custom_apps');

    // Collect all baselines stored as individual keys
    const baselines = {};
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('adlensfx_baseline_')) {
          const compositeId = key.replace('adlensfx_baseline_', '').replace('_', '/');
          baselines[compositeId] = JSON.parse(localStorage.getItem(key) || 'null');
        }
      }
    } catch {}

    await afetch(`${API_BASE}/api/profiles`, {
      method:  'PUT',
      headers: authHeaders(),
      body:    JSON.stringify({ initialized, removedBuiltins, customApps, baselines }),
    });
  } catch {
    // Network error — will be pushed on next mutation
  }
}

/* ─── Helpers ─────────────────────────────────────────────────────────────── */

function _readSet(key) {
  try { return new Set(JSON.parse(localStorage.getItem(key) || '[]')); } catch { return new Set(); }
}
function _writeSet(key, set) {
  try { localStorage.setItem(key, JSON.stringify([...set])); } catch {}
}
function _readObj(key) {
  try { return JSON.parse(localStorage.getItem(key) || '{}'); } catch { return {}; }
}
function _writeObj(key, obj) {
  try { localStorage.setItem(key, JSON.stringify(obj)); } catch {}
}
